import React, { useState, useEffect, useRef } from 'react';
import {
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Sparkles,
  BookOpen,
  Calculator,
  Users,
  Bot,
  ClipboardList,
  BookCopy,
  StickyNote,
  Target,
  CheckCircle2,
  Check,
  Mic,
  Smartphone,
  Download,
  ArrowRight
} from 'lucide-react';
import {
  useClerk,
  useAuth,
} from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import StorySection from '../components/CombinedSection';
import ReviewsSection from '../components/ReviewsSection';
import ManagementConsultingPrep from '../components/ManagementConsultingPrep';
import { LinkedinIcon, Twitter, Instagram } from 'lucide-react';

/* ------------------ GradientButton Component ------------------ */
const GradientButton = ({ children, className = '', ...props }) => (
  <button
    className={`relative group overflow-hidden bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg ${className}`}
    {...props}
  >
    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
    {children}
  </button>
);

/* FeatureCard removed — replaced by interactive FeaturesExplorer inline */

/* ------------------ PricingFeature Component ------------------ */
const PricingFeature = ({ children }) => (
  <li className="flex items-center space-x-3">
    <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
      <CheckCircle2 className="w-4 h-4 text-white" />
    </div>
    <span className="text-gray-600">{children}</span>
  </li>
);

/* ------------------ App Coming Soon (iOS) Section with Carousel ------------------ */
const APP_SCREENS = [
  { src: '/images/app/HOME.PNG', label: 'Home' },
  { src: '/images/app/CASE_LIBRARY.PNG', label: 'Case Library' },
  { src: '/images/app/CASE.PNG', label: 'Case Study' },
  { src: '/images/app/AI_CHAT.PNG', label: 'AI Coach' },
  { src: '/images/app/LESSON.PNG', label: 'Lessons' },
  { src: '/images/app/MATH.PNG', label: 'Math Practice' },
];

const AppComingSoonSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(() => {
      setActiveIndex((i) => (i + 1) % APP_SCREENS.length);
    }, 4000);
    return () => clearInterval(t);
  }, [isPaused]);

  return (
    <section id="app-coming-soon" className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-violet-700 via-purple-700 to-indigo-800" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(255,255,255,0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_80%_50%,rgba(139,92,246,0.3),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_80%,rgba(99,102,241,0.2),transparent)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white/95 text-sm font-semibold mb-6 shadow-lg">
            <Smartphone className="w-4 h-4" />
            Free on the App Store
          </div>
          <h2 className="text-3xl font-bold text-white sm:text-4xl md:text-5xl lg:text-6xl tracking-tight">
            Prep Anywhere. Anytime.
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            Squeeze in a case study on your commute, drill math problems between classes, or chat with your AI coach from anywhere.
            Aurora Prep Mobile puts serious consulting prep in your pocket — completely free.
          </p>
        </div>

        {/* Two-column: Video + Phone */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          {/* Left: App Demo Video */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md group">
              <div className="absolute -inset-2 bg-gradient-to-r from-white/20 via-purple-300/20 to-pink-300/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  poster="/images/app/HOME.PNG"
                >
                  <source src="/images/Aurora Prep (New iOS App).MP4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>

          {/* Right: Phone Carousel + On-the-go benefits */}
          <div
            className="flex flex-col items-center lg:items-start"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative w-full max-w-[260px] sm:max-w-[280px] mx-auto lg:mx-0" style={{ perspective: '1200px' }}>
              <div
                className="relative rounded-[2.75rem] bg-gradient-to-b from-gray-800 to-gray-900 p-2.5 sm:p-3 transition-transform duration-500 hover:scale-[1.02]"
                style={{
                  transform: 'rotateY(-10deg) rotateX(3deg)',
                  boxShadow: '0 50px 80px -20px rgba(0,0,0,0.45), 0 30px 50px -30px rgba(88,28,135,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
                }}
              >
                <div className="absolute -inset-[1px] rounded-[2.5rem] bg-gradient-to-b from-white/25 to-transparent opacity-70 pointer-events-none" />
                <div className="relative aspect-[9/19] rounded-[2.25rem] overflow-hidden bg-gray-950">
                  {APP_SCREENS.map((screen, i) => (
                    <div
                      key={screen.src}
                      className="absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
                      style={{
                        opacity: i === activeIndex ? 1 : 0,
                        transform: `translateX(${(i - activeIndex) * 15}%) scale(${i === activeIndex ? 1.07 : 1.04})`,
                        zIndex: i === activeIndex ? 10 : 0,
                      }}
                    >
                      <img
                        src={screen.src}
                        alt={screen.label}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-7 bg-gray-900 rounded-b-2xl z-20 pointer-events-none border border-gray-800" />
              </div>
              <div
                className="absolute left-1/2 top-full w-[80%] h-12 -translate-x-1/2 translate-y-1 bg-gradient-to-b from-black/15 to-transparent rounded-b-xl pointer-events-none"
                aria-hidden
              />
            </div>

            {/* On-the-go benefits */}
            <div className="mt-10 space-y-4 max-w-sm mx-auto lg:mx-0">
              {[
                'Practice case studies on the train or between classes',
                'Quick math drills whenever you have 5 minutes',
                'AI coach available 24/7 right in your pocket',
                'Track your progress and pick up where you left off',
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slider controls */}
        <div className="w-full max-w-xl mx-auto">
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            {APP_SCREENS.map((screen, i) => (
              <button
                key={screen.src}
                type="button"
                aria-label={`Show ${screen.label}`}
                onClick={() => setActiveIndex(i)}
                className={`flex flex-col items-center gap-2 rounded-2xl px-4 py-3 transition-all duration-300 ${i === activeIndex
                    ? 'bg-white/20 text-white ring-2 ring-white/40 shadow-lg'
                    : 'text-white/70 hover:bg-white/10 hover:text-white/90'
                  }`}
              >
                <span
                  className={`block w-2 h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-white scale-125' : 'bg-white/50'
                    }`}
                />
                <span className="text-xs font-medium hidden sm:block">{screen.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="Previous screen"
              onClick={() => setActiveIndex((i) => (i - 1 + APP_SCREENS.length) % APP_SCREENS.length)}
              className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-colors shadow-lg"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <button
              type="button"
              aria-label="Next screen"
              onClick={() => setActiveIndex((i) => (i + 1) % APP_SCREENS.length)}
              className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-colors shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const AuroraPrepConsultingLanding = () => {
  // ====================== HOOKS & STATES =======================
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isSignedIn, getToken } = useAuth();
  const { openSignIn, openSignUp } = useClerk();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const url = process.env.REACT_APP_BACK_END_URL || 'http://localhost:8080';

  const [notes, setNotes] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const hasCheckedAccessRef = useRef(false);

  const CHECKOUT_URL = 'https://auroraprep.lemonsqueezy.com/buy/1949da6c-f4ce-4551-b443-cab7e24871bb';

  // Check paywall and either go to app or redirect to checkout
  const checkAccessAndProceed = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/offerings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Access check failed');
      const data = await response.json();
      if (data.payingCustomer === true) {
        navigate('/main');
      } else {
        window.location.href = CHECKOUT_URL;
      }
    } catch (err) {
      console.error('Paywall check error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = async () => {
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    setLoading(true);
    setError('');
    await checkAccessAndProceed();
  };

  const handleGetStarted = async () => {
    if (!isSignedIn) {
      openSignUp();
      return;
    }
    setLoading(true);
    setError('');
    await checkAccessAndProceed();
  };

  // When user is already signed in (e.g. just returned from Clerk), run paywall check once
  useEffect(() => {
    if (!isSignedIn || hasCheckedAccessRef.current) return;
    hasCheckedAccessRef.current = true;
    setLoading(true);
    checkAccessAndProceed();
  }, [isSignedIn]);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ====================== DATA =======================
  const features = [
    {
      icon: BookOpen,
      title: 'Problem Solving Lessons',
      description:
        'Learn the strategic methods that consultants at top-tier firms leverage daily.',
      highlights: [
        'Structured frameworks for every case type',
        'Video walkthroughs with expert commentary',
        'Progressive difficulty from beginner to advanced'
      ]
    },
    {
      icon: Calculator,
      title: 'Quant & Math Drills',
      description:
        'Build confidence with targeted quantitative exercises, shortcuts, and in-depth solution walkthroughs.',
      highlights: [
        'Timed exercises simulating real interview pressure',
        'Shortcut techniques for rapid mental math',
        'Step-by-step solution breakdowns for every problem'
      ]
    },
    {
      icon: BookCopy,
      title: 'Practice Case Simulations',
      description:
        'Test your skills and gain instant feedback on how to improve your approach to casing.',
      highlights: [
        'AI-powered instant feedback on your approach',
        'Case library modeled on real consulting interviews',
        'Performance tracking across every attempt'
      ]
    },
    {
      icon: ClipboardList,
      title: 'Resume & Cover Letter Overhaul',
      description:
        'AI-enhanced and human-reviewed feedback to optimize your professional documents for top consulting firms.',
      highlights: [
        'AI analysis paired with human expert review',
        'Industry-specific templates and examples',
        'Targeted feedback for MBB and Big 4 firms'
      ]
    },
    {
      icon: Bot,
      title: '24/7 AI Tutor',
      description:
        'Instant guidance, clarifications, and feedback. Perfect for late-night study or quick concept checks.',
      highlights: [
        'Instant answers to any consulting concept',
        'Personalized guidance based on your progress',
        'Available whenever you need it, day or night'
      ]
    },
    {
      icon: Users,
      title: 'Behavioral Interview Prep',
      description:
        'Structured modules to craft compelling STAR stories and master those tricky “fit” questions.',
      highlights: [
        'STAR method frameworks with real examples',
        'Practice with AI-simulated interviewers',
        'Feedback on delivery, structure, and impact'
      ]
    },
    {
      icon: Target,
      title: 'Goal-Focused Roadmaps',
      description:
        'Customized study plans to keep you on track and align your practice with specific target firms.',
      highlights: [
        'Personalized plans for your target firms',
        'Milestone tracking and adaptive scheduling',
        'Aligned with real recruiting timelines'
      ]
    },
    {
      icon: StickyNote,
      title: 'Noted Integration',
      description:
        'Seamless note-taking and organization across all practice modules for continuous reference.',
      highlights: [
        'Capture insights as you learn across modules',
        'Organized by topic for quick review',
        'Export and share your study notes'
      ]
    }
  ];
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [mobileExpandedIndex, setMobileExpandedIndex] = useState(-1);

  // Trusted schools marquee
  const schoolLogos = [
    { name: 'Wharton', src: '/images/Wharton.png' },
    { name: 'UCLA', src: '/images/UCLA.png' },
    { name: 'Northwestern', src: '/images/Northwestern.png' },
    { name: 'NYU', src: '/images/NYU.png' },
    { name: 'MIT', src: '/images/MIT.png' },
    { name: 'Harvard', src: '/images/Harvard_1.png' },
    { name: 'Chicago Booth', src: '/images/Chicago_1.png' },
    { name: 'OU', src: '/images/OU.png' }
  ];
  const repeatedSchoolLogos = [...schoolLogos, ...schoolLogos]; // repeat array

  // Pricing values depending on billing cycle
  const displayPrice = billingCycle === 'monthly' ? '45' : '150';
  const priceSuffix = billingCycle === 'monthly' ? '/month' : '/year';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ======================== NAVIGATION ======================== */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo Section */}
            <div className="flex-shrink-0 flex items-center gap-3">
              <img
                src="/images/ap_logo.svg"
                alt="Aurora Prep Logo"
                className="h-8 w-auto"
              />
              <span className={`text-2xl font-bold transition-all duration-300 ${scrolled ? 'text-white' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-transparent bg-clip-text'}`}>
                Aurora Prep
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#features"
                className={`transition-colors ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Features
              </a>
              <a
                href="#app-coming-soon"
                className={`transition-colors ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                App
              </a>
              <a
                href="#pricing"
                className={`transition-colors ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Pricing
              </a>
              <a
                href="#voice-ai"
                className={`transition-colors flex items-center ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <Mic className="w-5 h-5 mr-1" />
                Voice AI
              </a>
              <a
                href="#noted-section"
                className={`transition-colors flex items-center ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <StickyNote className="w-5 h-5 mr-1" />
                Noted App
              </a>
              {!isSignedIn && (
                <button
                  onClick={handleLoginClick}
                  className={`px-4 py-2 transition-colors ${scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Login'}
                </button>
              )}
              <GradientButton onClick={handleGetStarted} disabled={loading}>
                {loading
                  ? 'Processing...'
                  : isSignedIn
                    ? 'Continue'
                    : 'Get Started'
                }
              </GradientButton>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden ${scrolled ? 'text-gray-300' : 'text-gray-600'}`}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute w-full bg-gray-950/95 backdrop-blur-lg">
            <div className="px-4 py-3 space-y-3">
              <a
                href="#features"
                className="block text-gray-300 hover:text-white"
              >
                Features
              </a>
              <a
                href="#app-coming-soon"
                className="block text-gray-300 hover:text-white"
              >
                App
              </a>
              <a
                href="#pricing"
                className="block text-gray-300 hover:text-white"
              >
                Pricing
              </a>
              <a
                href="#voice-ai"
                className="block text-gray-300 hover:text-white flex items-center"
              >
                <Mic className="w-5 h-5 mr-1" />
                Voice AI
              </a>
              <a
                href="#noted-section"
                className="block text-gray-300 hover:text-white flex items-center"
              >
                <StickyNote className="w-5 h-5 mr-1" />
                My Notes
              </a>
              {!isSignedIn && (
                <button
                  onClick={handleLoginClick}
                  className="block w-full text-left px-4 py-2 text-gray-300 hover:text-white"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Login'}
                </button>
              )}
              <GradientButton
                onClick={handleGetStarted}
                className="w-full"
                disabled={loading}
              >
                {loading
                  ? 'Processing...'
                  : isSignedIn
                    ? 'Continue'
                    : 'Get Started'
                }
              </GradientButton>
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </div>
          </div>
        )}
      </nav>
      {/* =========================================================== */}

      {/* Main content container (pushes footer to bottom if needed) */}
      <div className="flex-grow">
        {/* ======================== HERO SECTION ====================== */}
        <div className="relative bg-black overflow-hidden" style={{ minHeight: '100vh' }}>
          {/* Fullscreen looping video background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 0 }}
          >
            <source src="/images/nyc_hero_bg.mp4" type="video/mp4" />
          </video>
          {/* Dark gradient overlay for text contrast */}
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"
            style={{ zIndex: 1 }}
          />

          {/* Hero Content Container */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 flex items-center justify-center" style={{ zIndex: 2, minHeight: 'calc(100vh - 4rem)' }}>
            {/* Simple “translucent” dark box */}
            <div className="max-w-3xl mx-auto bg-black/50 rounded-xl p-8 sm:p-12 text-center shadow-2xl border border-white/10">
              <div className="flex items-center justify-center space-x-3 mb-8">
                <Sparkles className="w-6 h-6 text-indigo-200" />
                <span className="text-sm font-semibold text-indigo-100 tracking-wide uppercase">
                  Transform Your Consulting Career
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                <span className="block">Crack the Interview.</span>
                <span className="bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 text-transparent bg-clip-text">
                  Land the Offer.
                </span>
              </h1>

              <p className="max-w-2xl mx-auto text-lg sm:text-xl md:mt-5 text-gray-100">
                Aurora Prep helps aspiring consultants excel with case studies,
                AI-driven resume reviews, and tailored feedback for interview success—
                now with seamless Noted App integration for even more efficient preparation.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                {/* Login button - white background */}
                {!isSignedIn && (
                  <button
                    onClick={handleLoginClick}
                    className="w-full sm:w-auto px-6 py-3 bg-white text-gray-700 rounded-xl font-medium border border-gray-200 hover:border-gray-300 transition-colors duration-300"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Login'}
                  </button>
                )}

                {/* Continue/Get Started button - redirects to Clerk sign-up or /main if signed in */}
                <GradientButton
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto"
                  disabled={loading}
                >
                  <span className="flex items-center">
                    {loading
                      ? 'Processing...'
                      : isSignedIn
                        ? 'Continue'
                        : 'Get Started'
                    }
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </span>
                </GradientButton>
              </div>

              {error && <p className="text-red-200 text-sm mt-4">{error}</p>}
            </div>
          </div>
        </div>
        {/* =========================================================== */}

        {/* ======================== FEATURES SECTION ================== */}
        <style>{`
          @keyframes featureFadeIn {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .feature-fade-in { animation: featureFadeIn 0.4s ease-out both; }
        `}</style>
        <div id="features" className="py-28 relative">
          <div className="absolute inset-0 bg-[#FAFAFE]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* ---- Header ---- */}
            <div className="text-center mb-16 lg:mb-20">
              <span className="inline-block text-[11px] font-semibold tracking-[0.25em] uppercase text-indigo-600 bg-indigo-50 px-4 py-1.5 rounded-full mb-6">
                What's Included
              </span>
              <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl tracking-tight leading-tight">
                Build the Skills That{' '}
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Top Firms Demand
                </span>
              </h2>
              <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
                Our program covers every angle — from quant drills to behavioral mastery.
              </p>
            </div>

            {/* ---- Desktop: Interactive Tab Explorer ---- */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-0 items-stretch min-h-[480px]">
              {/* Left: Tab list */}
              <div className="col-span-5 xl:col-span-4 border-r border-gray-200/80 pr-0">
                <div className="space-y-1">
                  {features.map((feature, i) => {
                    const Icon = feature.icon;
                    const isActive = i === activeFeatureIndex;
                    return (
                      <button
                        key={i}
                        onClick={() => setActiveFeatureIndex(i)}
                        className={`w-full text-left px-6 py-4 rounded-l-xl flex items-center gap-4 transition-all duration-300 relative group
                          ${isActive
                            ? 'bg-white shadow-sm'
                            : 'hover:bg-gray-50/80'
                          }`}
                      >
                        <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-gradient-to-b from-indigo-500 to-purple-500 transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0'}`} />

                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300
                          ${isActive
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-500'
                            : 'bg-gray-100 group-hover:bg-gray-200/80'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} strokeWidth={1.5} />
                        </div>

                        <span className={`block text-[15px] font-medium transition-colors duration-300 min-w-0 ${isActive ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-800'}`}>
                          {feature.title}
                        </span>

                        <span className="ml-auto text-[11px] font-mono text-gray-300 flex-shrink-0">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: Detail panel */}
              <div className="col-span-7 xl:col-span-8 pl-12 flex items-center">
                <div key={activeFeatureIndex} className="feature-fade-in w-full">
                  {(() => {
                    const active = features[activeFeatureIndex];
                    const ActiveIcon = active.icon;
                    return (
                      <div>
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <ActiveIcon className="w-7 h-7 text-white" strokeWidth={1.5} />
                          </div>
                          <span className="text-sm font-mono text-indigo-400 tracking-wider">
                            {String(activeFeatureIndex + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}
                          </span>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 tracking-tight mb-4">
                          {active.title}
                        </h3>

                        <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
                          {active.description}
                        </p>

                        <div className="h-px bg-gradient-to-r from-gray-200 via-gray-200 to-transparent mb-8 max-w-sm" />

                        <ul className="space-y-4">
                          {active.highlights.map((h, j) => (
                            <li key={j} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-indigo-600" strokeWidth={2.5} />
                              </div>
                              <span className="text-gray-600 text-[15px]">{h}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* ---- Mobile / Tablet: Accordion ---- */}
            <div className="lg:hidden space-y-3">
              {features.map((feature, i) => {
                const Icon = feature.icon;
                const isOpen = mobileExpandedIndex === i;
                return (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden
                      ${isOpen
                        ? 'border-indigo-200 bg-white shadow-md shadow-indigo-500/[0.06]'
                        : 'border-gray-200/60 bg-white'
                      }`}
                  >
                    <button
                      onClick={() => setMobileExpandedIndex(isOpen ? -1 : i)}
                      className="w-full flex items-center gap-4 p-5 text-left"
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300
                        ${isOpen
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-500'
                          : 'bg-gray-100'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${isOpen ? 'text-white' : 'text-gray-500'}`} strokeWidth={1.5} />
                      </div>

                      <span className={`text-[15px] font-medium flex-1 ${isOpen ? 'text-gray-900' : 'text-gray-700'}`}>
                        {feature.title}
                      </span>

                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <div
                      className="transition-all duration-300 ease-in-out overflow-hidden"
                      style={{
                        maxHeight: isOpen ? '300px' : '0px',
                        opacity: isOpen ? 1 : 0,
                      }}
                    >
                      <div className="px-5 pb-5 pt-0">
                        <p className="text-gray-500 text-[15px] leading-relaxed mb-4">
                          {feature.description}
                        </p>
                        <ul className="space-y-3">
                          {feature.highlights.map((h, j) => (
                            <li key={j} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-indigo-600" strokeWidth={2.5} />
                              </div>
                              <span className="text-gray-600 text-sm">{h}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* =========================================================== */}

        {/* ======================== TRUSTED BY STUDENTS (MARQUEE) ==== */}
        <section className="py-20 bg-white relative overflow-hidden">
          {/* Add a small <style> block for marquee animation */}
          <style>
            {`
              @keyframes marquee {
                0%   { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}
          </style>

          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-600 text-xl font-semibold mb-6 tracking-wide">
              TRUSTED BY STUDENTS FROM
            </p>

            {/* Marquee Outer Container */}
            <div className="overflow-hidden relative w-full">
              {/* The actual “moving” container */}
              <div
                className="flex space-x-12 whitespace-nowrap"
                style={{
                  animation: 'marquee 20s linear infinite',
                }}
              >
                {repeatedSchoolLogos.map((school, i) => (
                  <img
                    key={i}
                    src={school.src}
                    alt={school.name}
                    className="h-8 w-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* =========================================================== */}

        {/* ======================== VIDEO DEMO SECTION =============== */}
        <div className="py-24 bg-white relative">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 opacity-50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                See Aurora Prep in Action
              </h2>
              <p className="mt-4 text-xl text-gray-600">
                Watch our platform transform the way you approach consulting interviews.
              </p>
            </div>
            <div className="flex justify-center">
              <div
                className="relative"
                style={{ height: '500px', width: '100%' }}
              >
                <video
                  className="absolute top-0 left-0 w-full h-full"
                  controls
                  poster="/images/poster-image.png"
                >
                  <source src="/images/auroraprep.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
        {/* =========================================================== */}

        {/* ======================== VOICE AI INTERACTIVE CASE SECTION == */}
        <section id="voice-ai" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left: Copy */}
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 text-sm font-semibold mb-6">
                  <Mic className="w-4 h-4" />
                  <span>New</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl lg:text-[2.5rem] leading-tight">
                  AI Real-Time Voice-to-Voice Interactive Case Interviews
                </h2>
                <p className="mt-5 text-lg text-gray-600 leading-relaxed">
                  Practice your strategic thinking with live, conversational case simulations.
                  Speak naturally with your AI interviewer, get instant feedback, and build
                  confidence for the real thing—all in real time.
                </p>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Natural back-and-forth dialogue with an AI interviewer</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Real-time voice input and response—no typing required</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Structured case scenarios designed to mirror top-firm interviews</span>
                  </li>
                </ul>
                <div className="mt-10">
                  <GradientButton onClick={handleGetStarted} disabled={loading}>
                    Try Voice Case Interviews
                  </GradientButton>
                </div>
              </div>
              {/* Right: Image card */}
              <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                <div className="relative w-full max-w-xl group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity duration-300" />
                  <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 bg-white">
                    <img
                      src="/images/hsc_voice_ai.png"
                      alt="AI Real-Time Voice-to-Voice Interactive Case Interview with Aurora Prep"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* =========================================================== */}

        {/* ======================== APP ON THE GO (iOS) ============== */}
        <AppComingSoonSection />
        {/* =========================================================== */}

        {/* ======================== NOTED SECTION (IF SIGNED IN) ===== */}
        <div id="noted-section" className="py-24 relative bg-gradient-to-br from-white to-gray-100">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Background Layer for Glassmorphism */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-100 to-purple-100 opacity-20 pointer-events-none rounded-2xl" />
            <div className="relative bg-white/70 backdrop-blur-md shadow-xl rounded-2xl p-8 md:p-12">
              {/* Two-column layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                {/* Left Column: Image or Screenshot */}
                <div className="flex justify-center">
                  <img
                    src="/images/Noted.png"
                    alt="Noted Integration Preview"
                    className="rounded-xl shadow-lg border border-gray-200"
                  />
                </div>
                {/* Right Column: Content & Notes Display */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    Seamless Notes with <span className="text-indigo-600">Noted</span>
                  </h2>
                  <p className="text-gray-600 text-lg mb-6">
                    Organize all your insights, frameworks, and interview strategies in one place.
                    Access and update your notes in real time without ever leaving the Aurora Prep platform.
                  </p>

                  {notes && notes.length > 0 ? (
                    <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                      {notes.map((note, idx) => (
                        <div
                          key={note.id || idx}
                          className="p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                        >
                          <h3 className="text-md font-semibold text-gray-800 mb-1">
                            {note.title || `Note #${idx + 1}`}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {note.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent italic">
                      You don’t have any notes yet. Start practicing to create your first note!
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
        {/* =========================================================== */}

        {/* ======================== PRICING SECTION =================== */}
        <div id="pricing" className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 opacity-50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                Choose the Plan That Fits Your Prep Style
              </h2>
              <p className="mt-4 text-xl text-gray-600">
                Start free with our mobile app, or unlock the full platform for a comprehensive edge.
              </p>
            </div>

            {/* Toggle for Monthly/Yearly — only applies to Premium */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex items-center space-x-4">
                <span
                  className={`cursor-pointer font-medium ${billingCycle === 'monthly' ? 'text-indigo-600' : 'text-gray-500'
                    }`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  Monthly
                </span>
                <div
                  className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in"
                  onClick={() =>
                    setBillingCycle((prev) => (prev === 'monthly' ? 'annual' : 'monthly'))
                  }
                >
                  <input
                    type="checkbox"
                    name="toggle"
                    id="toggle"
                    checked={billingCycle === 'annual'}
                    onChange={() => { }}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    style={{
                      top: '0px',
                      left: billingCycle === 'annual' ? 'calc(100% - 1.5rem)' : '0',
                    }}
                  />
                  <label
                    htmlFor="toggle"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  />
                </div>
                <span
                  className={`cursor-pointer font-medium ${billingCycle === 'annual' ? 'text-indigo-600' : 'text-gray-500'
                    }`}
                  onClick={() => setBillingCycle('annual')}
                >
                  Yearly
                </span>
              </div>
            </div>

            {/* Two-column pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* FREE — Mobile App */}
              <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 flex flex-col">
                <div className="px-6 py-8 flex-grow">
                  <div className="flex items-center space-x-2 mb-2">
                    <Smartphone className="w-5 h-5 text-purple-600" />
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Aurora Prep Mobile
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">Perfect for prepping on the go</p>
                  <div className="flex items-baseline mb-8">
                    <span className="text-5xl font-extrabold text-gray-900">Free</span>
                  </div>
                  <ul className="space-y-4">
                    <PricingFeature>Case studies &amp; case library</PricingFeature>
                    <PricingFeature>Math practice drills</PricingFeature>
                    <PricingFeature>AI coach for quick guidance</PricingFeature>
                    <PricingFeature>Structured lessons &amp; frameworks</PricingFeature>
                    <PricingFeature>Study anywhere, anytime on iOS</PricingFeature>
                    <PricingFeature>Track your progress on the go</PricingFeature>
                  </ul>
                </div>
                <div className="px-6 py-8 bg-gray-50 border-t border-gray-100">
                  <div className="text-center">
                    <a
                      href="https://apps.apple.com/us/app/aurora-prep/id6760251017"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl font-medium border-2 border-purple-500 text-purple-600 hover:bg-purple-50 transition-colors duration-300"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Free on the App Store
                    </a>
                    <p className="text-gray-400 text-xs mt-3">Available now on iOS</p>
                  </div>
                </div>
              </div>

              {/* PREMIUM — Full Platform */}
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-indigo-200 flex flex-col ring-2 ring-indigo-100">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 opacity-50" />
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                <div className="relative px-6 py-8 flex-grow">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="w-5 h-5 text-indigo-600" />
                      <h3 className="text-2xl font-semibold text-gray-900">
                        Premium Platform
                      </h3>
                    </div>
                    <span className="px-3 py-1 text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full">
                      Recommended
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">Full web platform for comprehensive prep</p>
                  <div className="flex items-baseline mb-8">
                    <span className="text-5xl font-extrabold text-gray-900">
                      ${displayPrice}
                    </span>
                    <span className="ml-2 text-gray-500">{priceSuffix}</span>
                  </div>
                  <p className="text-sm font-medium text-indigo-600 mb-4 flex items-center">
                    <ArrowRight className="w-4 h-4 mr-1" />
                    Everything in the free app, plus:
                  </p>
                  <ul className="space-y-4">
                    <PricingFeature>AI Real-Time Voice Case Interviews</PricingFeature>
                    <PricingFeature>Resume &amp; Cover Letter Reviewer</PricingFeature>
                    <PricingFeature>Simulated Case Problems with Detailed Feedback</PricingFeature>
                    <PricingFeature>Complete Frameworks &amp; Study Guides</PricingFeature>
                    <PricingFeature>Unlimited Access to the AI Tutor</PricingFeature>
                    <PricingFeature>Free Access to Noted App</PricingFeature>
                    <PricingFeature>Goal-Focused Roadmaps &amp; Study Plans</PricingFeature>
                  </ul>
                </div>
                <div className="relative px-6 py-8 bg-gray-50 border-t border-gray-100">
                  <div className="text-center">
                    <GradientButton
                      onClick={handleGetStarted}
                      className="w-full"
                      disabled={loading}
                    >
                      {loading
                        ? 'Processing...'
                        : isSignedIn
                          ? 'Continue'
                          : 'Get Started Now'
                      }
                    </GradientButton>
                    {error && (
                      <p className="text-red-500 text-sm mt-2">{error}</p>
                    )}
                    <p className="text-base font-medium text-indigo-600 mt-4">
                      For Enterprise or University pricing please contact support@auroraprep.com!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Sections */}
        <StorySection />
        <ReviewsSection />

        {/* ======================== FINAL CTA SECTION =============== */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          <div className="relative max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block mb-1">Ready to Accelerate Your Career?</span>
              <span className="block">Start your Consutling Career today.</span>
            </h2>
            <p className="mt-4 text-lg text-indigo-100">
              Try Today! Cancel anytime.
            </p>

            <GradientButton
              onClick={handleGetStarted}
              className="mt-8 bg-white text-gray-900 px-8 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-300"
              disabled={loading}
            >
              {loading
                ? 'Processing...'
                : isSignedIn
                  ? 'Continue'
                  : 'Sign Up Today!'
              }
            </GradientButton>

            {error && (
              <p className="text-red-500 text-sm mt-2">{error}</p>
            )}

            {/* Social Media Icons */}
            <div className="mt-8 flex justify-center space-x-6">
              <a
                href="https://www.linkedin.com/company/aurora-prep/"
                className="text-white hover:text-indigo-100"
              >
                <LinkedinIcon className="w-6 h-6" />
              </a>
              <a
                href="https://x.com/Aurora_Prep"
                className="text-white hover:text-indigo-100"
              >
                <Twitter className="w-6 h-6" />
              </a>
              <a
                href="https://www.instagram.com/auroraprep/"
                className="text-white hover:text-indigo-100"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>

            {/* Copyright Notice */}
            <div className="mt-6 text-sm text-white">
              © Aurora Prep 2026. All rights reserved.
            </div>
          </div>
        </div>
      </div>

      {/* ======================== FOOTER WITH LINKS =============== */}
      <footer className="bg-gray-900 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-center sm:justify-between items-center text-gray-400 text-sm space-y-3 sm:space-y-0">
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-center">
            <Link to="/terms" className="hover:text-white whitespace-nowrap">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-white whitespace-nowrap">Privacy Policy</Link>
            <a href="/privacy-policy-mobile.html" className="hover:text-white whitespace-nowrap" target="_blank" rel="noopener noreferrer">
              Privacy Policy (Mobile App)
            </a>
            <a href="/support-mobile.html" className="hover:text-white whitespace-nowrap" target="_blank" rel="noopener noreferrer">
              Support (Mobile App)
            </a>
          </div>
          <div>
            <span>© 2026 Aurora Prep</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuroraPrepConsultingLanding;
